/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { ActionContext, Module } from 'vuex';
import { checkSuperCardTabShow } from '@/api/rights';
import TabConfigs from '@/shared/utils/tabConfigs';
import { NavIntentEnums } from '@/shared/utils/tabConfigs/types';
import URLUtils from '@/shared/utils/URLUtils';
import platform from '@/shared/utils/platform';
import { skNativeContainer } from '@/api/native/container';
import { PageThemeTypeEnum } from '@/enums/pageThemeType';
import type { Store } from 'vuex';
import { ubtSendError } from '@/shared/utils/ubt';
import rootStore from '@/store';
import { getBusinessInfo, getSyncUserInfo } from '@/api/common';
import { CREDIT_RATE_CEIL, CREDIT_RATE_FLOOR } from '@/constant/system';
import { BusinessInfoExtra, PageSourceEnum } from '@/api/common/types';
import { ThemeEnum, ThemeManager } from '@hb/coffee';
import { isIPOrigin } from '@/constant';
import generateMutations, { UPDATE_STATE_TYPE } from '../generate-mutations';
import RootStateType from '../types';
import type { DispatchActionsType, StateType } from './types';

const defaultBusinessCreditInfo = {
  retentionPopupAmount: '',
  rateCeil: CREDIT_RATE_CEIL,
  rateFloor: CREDIT_RATE_FLOOR,
};
/** 是否初始化主题类型中 */
let isGetExperimentThemeTyping = false;

const moduleStore: Module<StateType, RootStateType> = {
  namespaced: true,

  state: {
    originUserInfo: Object.create(null),
    deviceInfo: Object.create(null),
    /** 平台返回的设备信息，主要用于获取clientId */
    platformDeviceInfo: Object.create(null),
    cityInfo: Object.create(null),
    h5UUids: Object.create(null),
    uid: '',
    userId: '',
    token: '',
    activeTab: 0,
    isTestUser: false,
    isLogoutUser: false,
    isSupportWebp: false,
    fingerprintHash: Object.create(null),
    repayState: Object.create(null),
    progress: '',
    existTianjiang: true,
    abRes: new Map(),
    backByJumpToNew: false,
    fakeToken: false,
    forceUpdateKey: '',
    superMemberTabInfo: null,
    tabInfoList: [],
    isHybridAppMode: Boolean(URLUtils.getQueryMap().hybridAppMode),
    coffeeNavBarHeight: 0,
    hitRepaymentExperiment: undefined,
    /** 命中最高额度实验组 */
    hitMaxQuotaExperiment: false,
    pageThemeType: undefined,
    isWebviewSkNativeContainer: undefined,
    businessInfoExtra: { creditInfo: defaultBusinessCreditInfo },
    syncUserInfoCache: null,
  },

  getters: {
    devicesJSON(state: StateType): NativeModel.DeviceInfo & { h5uuid: string } {
      if (state.deviceInfo && Object.keys(state.deviceInfo).length) {
        return { ...state.deviceInfo, h5uuid: state.h5UUids, ...state.fingerprintHash };
      }

      // 如果没有拿到deviceInfo，则直接返回空对象不用做api上传，避免覆盖已有的数据
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return Object.create(null);
    },
  },
  actions: {
    /** 获取超级会员tabbar是否展示,并处理成tabbarlist返回 */
    async getMemberTabInfo(
      context: ActionContext<StateType, RootStateType>,
    ) {
      try {
        /** 微信小程序无权益相关，无需请求权益检测 */
        if (!platform.isWechatMini) {
          const checkTabRes = await checkSuperCardTabShow();
          const {
            checkResult, iconCode, iconContent, packageType, packageBuyStatus,
          } = checkTabRes;
          if (checkResult
          && !TabConfigs.filter((item) => item.id === NavIntentEnums.financeMember).length
          ) {
            TabConfigs.splice(1, 0, {
              id: NavIntentEnums.financeMember,
              name: '会员',
              icon: 'nav-member',
              buttonName: '臻有钱_会员',
              clickMouduleId: 'finance_loan_tab_member',
              badgeType: iconCode,
              badgeContent: iconContent,
              extendInfo: {
                packageType,
                packageBuyStatus,
              },
            });
          }
        }

        context.dispatch(UPDATE_STATE_TYPE, {
          tabInfoList: TabConfigs,
        });
      } catch (e) {
        // 兜底为不展示会员
        context.dispatch(UPDATE_STATE_TYPE, {
          tabInfoList: TabConfigs,
        });
      }
    },
    /** 查询是否命中页面的主题AB测试 */
    async getExperimentPageThemeType(
      context: ActionContext<StateType, RootStateType>,
      payload?: {
        /** 是否预设默认主题，默认无预设值 */
        isPresetDefault?: boolean;
      },
    ) {
      try {
        const sessionStorageKey = 'finance_web_pageThemeType';
        const { isPresetDefault = false } = payload || {};
        /** 设置页面主题类型 */
        const setPageThemeType = (pageThemeType: PageThemeTypeEnum) => {
          // eslint-disable-next-line no-console
          console.error('---getExperimentPageThemeType---', pageThemeType);
          isGetExperimentThemeTyping = false;
          ThemeManager.setTheme(pageThemeType as unknown as ThemeEnum);
          // 同步设置sessionStorage，用于其他页面读取主题类型（AppFinanceLoanH5）
          sessionStorage.setItem(sessionStorageKey, pageThemeType);
          // 保存全局store-主题类型
          context.dispatch(UPDATE_STATE_TYPE, {
            pageThemeType,
          });

          return Promise.resolve();
        };

        /**
         * 如果已初始过主题标致，则不在重复请求分流
         * 避免因异常情况导致用户前后页面主题不一致
         */
        if (context.state.pageThemeType || isGetExperimentThemeTyping) {
          return Promise.resolve();
        }

        const urlQuery = URLUtils.getQueryMap();
        const themeTypeCache = sessionStorage.getItem(sessionStorageKey) || urlQuery.themeType || '';

        /** 如果sessionStorage中存在缓存，则使用缓存生效 */
        if (themeTypeCache) {
          return setPageThemeType(themeTypeCache as PageThemeTypeEnum);
        }

        /** 调用场景指定：默认主题 */
        if (isPresetDefault === true) {
          return setPageThemeType(PageThemeTypeEnum.DEFAULT);
        }

        isGetExperimentThemeTyping = true;

        /** url参数设置主题，仅针对测试开放 */
        if (urlQuery.pageThemeType) {
          return setPageThemeType(urlQuery.pageThemeType as PageThemeTypeEnum);
        }
        const { isAutarkyUpgradeUser } = rootStore.state.userAutarkyStatus || {};

        /** 如果当前是IP地址，则进入蓝色主题，方便本地mock或者fat运行时 浏览器调试 */
        if (isIPOrigin) {
          return setPageThemeType(PageThemeTypeEnum.BLUE_THEME);
        }

        /**
         * 非哈啰出行App或者非臻享花App或者自营升级用户，且非微信小程序，则进入默认主题
         */
        if ((!(platform.isEasyBike || platform.isSkzhenxhApp)
          || isAutarkyUpgradeUser
        )
          && !platform.isWechatMini
        ) {
          return setPageThemeType(PageThemeTypeEnum.DEFAULT);
        }

        /** 容器化流量，tabbar容器未适配蓝色版，所以不参与ab实验 && 取1%用户(userId结尾为'57')橙色试点 */
        if (await skNativeContainer()) {
          return setPageThemeType(
            String(context.state.userId).endsWith('57') ? PageThemeTypeEnum.ORANGE_THEME : PageThemeTypeEnum.DEFAULT,
          );
        }

        return setPageThemeType(PageThemeTypeEnum.BLUE_THEME);
      } catch (e) {
        ubtSendError({
          name: 'getExperimentPageThemeType error',
          message: (e as Error)?.message,
        });
        isGetExperimentThemeTyping = false;
        return Promise.resolve();
      }
    },
    /**
     * 获取当前是否在数科容器化Webview容器中：
     * 请在页面mounted调用，若提前调用bridge可能会返回错误的值
     */
    async getIsWebviewSkNativeContainer(context: ActionContext<StateType, RootStateType>) {
      if (context.state.isWebviewSkNativeContainer === undefined) {
        context.dispatch(UPDATE_STATE_TYPE, {
          isWebviewSkNativeContainer: await skNativeContainer(),
        });
      }

      return Promise.resolve(context.state.isWebviewSkNativeContainer);
    },
    /** 初始化信贷业务-公共信息 */
    async getBusinessInfoExtra(
      context: ActionContext<StateType, RootStateType>,
      payload?: {
        pageSource?: PageSourceEnum;
      },
    ) {
      let businessInfo = {} as BusinessInfoExtra;

      /** 已成功同步过数据，且来源不是（首页、我的页面），则直接返回不再请求 */
      if (
        context.state.businessInfoExtra?.syncApiSuccess
        && (
          !payload?.pageSource
          || ![PageSourceEnum.HOME, PageSourceEnum.MY_PROFILE].includes(payload.pageSource)
        )
      ) return;

      try {
        businessInfo = await getBusinessInfo(payload?.pageSource);

        businessInfo.syncApiSuccess = true;
      } catch (error) {
        ubtSendError({
          name: 'getBusinessInfoExtra error',
          message: (error as Error)?.message,
        });
      } finally {
        /** 异常兜底，避免因接口异常的情况下导致业务展示异常 */
        if (!businessInfo || !businessInfo.creditInfo) {
          businessInfo = {
            ...(businessInfo || {}),
            creditInfo: defaultBusinessCreditInfo,
          };
        }

        context.dispatch(UPDATE_STATE_TYPE, {
          businessInfoExtra: businessInfo,
        });
      }
    },
    /** 通知资产服务，调用平台同步拉取二要素、异步拉取ocr照片 */
    async getSyncUserInfo(context: ActionContext<StateType, RootStateType>) {
      try {
        if (context.state.syncUserInfoCache) return;

        const data = await getSyncUserInfo();

        context.dispatch(UPDATE_STATE_TYPE, {
          syncUserInfoCache: Promise.resolve(data),
        });
      } catch (error) {
        ubtSendError({
          name: 'getSyncUserInfo-error',
          message: (error as Error)?.message,
        });
      }
    },
  },
};

export default generateMutations<StateType>(moduleStore);

export const generateDispatchActions = (store: Store<RootStateType>): DispatchActionsType => ({
  getExperimentPageThemeType:
    (payload?: { isPresetDefault?: boolean }): Promise<void> => store.dispatch('base/getExperimentPageThemeType', payload) as unknown as Promise<void>,
  getIsWebviewSkNativeContainer:
    (): Promise<boolean> => store.dispatch('base/getIsWebviewSkNativeContainer') as unknown as Promise<boolean>,
  getBusinessInfoExtra:
    (payload?: { pageSource?: PageSourceEnum }): Promise<void> => store.dispatch('base/getBusinessInfoExtra', payload) as unknown as Promise<void>,
  getSyncUserInfo:
    (): Promise<void> => store.dispatch('base/getSyncUserInfo') as unknown as Promise<void>,
});
